import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DetailToolBridgeParamsPanel from '@/pages/ipe/workbench/components/DetailToolBridgeParamsPanel';
import DetailToolBridgeSourcesPreview from '@/pages/ipe/workbench/components/DetailToolBridgeSourcesPreview';
import { TOOL_NAV_ITEMS } from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import {
  detailToolBridgeHasParamsPanel,
  detailToolBridgeUsesStudioShell,
} from '@/pages/ipe/workbench/utils/detailToolBridgeParamsSupport';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import '@/styles/ipe/workbench/modals/DetailToolBridgeModal.css';
import '@/styles/shared/modal-footer-confirm-button.css';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/ImageFissionPage.css';
import '@/styles/ipe/workbench/PodBatchComposePage.css';

/** 稳定的空数组：默认参数若写成 `= []`，每次渲染都是新引用，会击穿下游 useEffect 依赖导致渲染死循环。 */
const EMPTY_LIST = [];

/**
 * 详情页功能联动：确认后在目标功能创建任务或写入商品库；
 * Toast 可点功能名跳转（不自动进详情 / 商品库，不打开历史）。
 * 主图创作 / AI文案走独立参数弹窗。
 * AI文案请走 {@link AiCopywritingTemplatePickerModal}，勿经本弹窗。
 */
export default function DetailToolBridgeModal({
  open = false,
  targetNavId = '',
  sources = EMPTY_LIST,
  studioParams = {},
  imageTemplates = EMPTY_LIST,
  onStudioParamsChange,
  onImageTemplatesLoaded,
  confirming = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !confirming) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirming, onClose]);

  if (!open) return null;

  const tool = TOOL_NAV_ITEMS[targetNavId] || null;
  const toolLabel = tool?.label || targetNavId || t('detailToolBridge.fallbackTool');
  const list = Array.isArray(sources) ? sources.filter((s) => s?.url || s?.previewUrl) : [];
  const hasParamsPanel = detailToolBridgeHasParamsPanel(targetNavId);
  const useStudioShell = detailToolBridgeUsesStudioShell(targetNavId);
  const confirmDisabled = confirming || list.length === 0;

  const handleConfirm = () => {
    const nav = String(targetNavId || '').trim();
    if (nav === 'platformListingJobs') {
      navigate(IPE_WORKBENCH_PATH, { state: { activeNav: 'platformListingJobs' } });
      onClose?.();
      return;
    }
    onConfirm?.();
  };

  const confirmSubtitle = targetNavId === 'platformListing'
    ? t('detailToolBridge.subtitlePlatform', { count: list.length })
    : t('detailToolBridge.subtitleImages', { count: list.length });

  const shellClass = [
    'detail-tool-bridge-modal',
    useStudioShell ? ' detail-tool-bridge-modal--studio' : '',
    targetNavId === 'img2img' ? ' detail-tool-bridge-modal--img2img' : '',
    (targetNavId === 'imageFission' || targetNavId === 'patternFission')
      ? ' detail-tool-bridge-modal--img-fission'
      : '',
    targetNavId === 'podProductDesign' ? ' detail-tool-bridge-modal--pod' : '',
  ].join('');

  return createPortal(
    (
      <div
        className="detail-tool-bridge-modal__backdrop"
        role="presentation"
        onClick={() => {
          if (!confirming) onClose?.();
        }}
      >
        <div
          className={shellClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby="detail-tool-bridge-title"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="detail-tool-bridge-modal__head">
            <div>
              <h2 id="detail-tool-bridge-title" className="detail-tool-bridge-modal__title">
                {toolLabel}
              </h2>
              <p className="detail-tool-bridge-modal__subtitle">
                {confirmSubtitle}
              </p>
            </div>
            <button
              type="button"
              className="detail-tool-bridge-modal__close"
              onClick={onClose}
              disabled={confirming}
              aria-label={t('commonActions.close')}
            >
              <X size={18} aria-hidden />
            </button>
          </header>

          <div className="detail-tool-bridge-modal__body">
            <DetailToolBridgeSourcesPreview
              sources={list}
              title={t('detailToolBridge.sourcesTitle')}
            />

            {!hasParamsPanel && tool?.cardDesc ? (
              <p className="detail-tool-bridge-modal__desc">{tool.cardDesc}</p>
            ) : null}

            {hasParamsPanel ? (
              <div className="detail-tool-bridge-modal__params">
                <DetailToolBridgeParamsPanel
                  navId={targetNavId}
                  studioParams={studioParams}
                  onStudioParamsChange={onStudioParamsChange}
                  disabled={confirming}
                  imageTemplates={imageTemplates}
                  variant="modal"
                  onImageTemplatesLoaded={onImageTemplatesLoaded}
                />
              </div>
            ) : null}
          </div>

          <footer className="detail-tool-bridge-modal__footer">
            <button
              type="button"
              className="detail-tool-bridge-modal__btn detail-tool-bridge-modal__btn--ghost"
              onClick={onClose}
              disabled={confirming}
            >
              {t('commonActions.cancel')}
            </button>
            <button
              type="button"
              className="detail-tool-bridge-modal__btn detail-tool-bridge-modal__btn--primary"
              onClick={handleConfirm}
              disabled={confirmDisabled}
            >
              {confirming ? t('commonActions.processing') : t('detailToolBridge.confirmWithTool', { tool: toolLabel })}
            </button>
          </footer>
        </div>
      </div>
    ),
    document.body,
  );
}
